import { OPTION_GROUP_QUESTIONS } from '@/app/crunches/[id]/InfoPanel/constants'
import {
  ChecklistFormOptions,
  ChecklistOptions,
  DocumentOptions,
  DownloadFormValues,
} from '@/app/crunches/[id]/Subheader/DownloadDialog'
import {
  Button,
  Checkbox,
  cn,
  Icon,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  Tooltip,
} from '@/dc-shared-web/src'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/dc-shared-web/src/components/shadcn/form'
import { TooltipTrigger } from '@/dc-shared-web/src/components/shadcn/tooltip'
import { ChecklistDataDownload } from '@/dc-shared-web/src/hooks/api/document/postDownloadPdf'
import { faInfoCircle } from '@document-crunch/pro-regular-svg-icons'
import { TooltipProvider } from '@radix-ui/react-tooltip'
import { UseFormReturn } from 'react-hook-form'

const shouldDisableInclude = (
  fieldName: string,
  checklist: ChecklistFormOptions
) => {
  // List of all option fields
  const optionFields = [
    'include_option1',
    'include_option2',
    'include_option3',
    'include_no_answer',
  ]

  // If an option field is being enabled, we shouldn't disable include
  if (
    optionFields.includes(fieldName) &&
    !checklist[fieldName as keyof ChecklistFormOptions]
  ) {
    return false
  }

  // Get all other option fields except the current one
  const otherOptions = optionFields.filter((field) => field !== fieldName)

  // Check if all other options are already false and current field is being disabled
  return otherOptions.every(
    (option) => !checklist[option as keyof ChecklistFormOptions]
  )
}

export default function DownloadDialogExpanded({
  activeSection,
  form,
  checklistOptions,
  documentOption,
  setDefaultValuesCache,
  saveChecklistOnCache,
  onChange,
}: {
  activeSection: string
  form: UseFormReturn<DownloadFormValues>
  checklistOptions: ChecklistOptions[]
  documentOption: DocumentOptions
  setDefaultValuesCache: (values: DownloadFormValues) => void
  saveChecklistOnCache: (checklist: ChecklistDataDownload) => void
  onChange: (fieldName: string) => void
}) {
  const onPdfChange = (val: string) => {
    form.setValue('useOriginalDoc', val === 'original')
    form.setValue('document', true)

    setDefaultValuesCache(form.getValues())
  }

  const onChecklistChange = (fullFieldName: string) => {
    const fieldName = fullFieldName.split('.')[2]
    const fieldIndex = parseInt(fullFieldName.split('.')[1])

    form.setValue(
      'checklists',
      form.getValues('checklists').map((c, index) => {
        if (index === fieldIndex) {
          const willBeDisabled = c[fieldName as keyof ChecklistFormOptions]

          if (
            !c.include &&
            !shouldDisableInclude(fieldName, c) &&
            !c[fieldName as keyof ChecklistFormOptions]
          ) {
            c.include = true
          }

          if (willBeDisabled && shouldDisableInclude(fieldName, c)) {
            c.include = false
          }

          const updatedValue = {
            ...c,
            [fieldName as keyof ChecklistFormOptions]:
              !c[fieldName as keyof ChecklistFormOptions],
          }

          if (
            !Object.keys(updatedValue as ChecklistFormOptions).some(
              (key) =>
                key !== 'include' &&
                key !== 'uuid' &&
                (updatedValue as any)[key]
            )
          ) {
            updatedValue.include = false
          }

          return updatedValue
        }

        return c
      })
    )

    saveChecklistOnCache(form.getValues('checklists')[fieldIndex])
  }
  return (
    <>
      {activeSection.includes('checklists') && (
        <div className='tw-w-1/2 tw-max-w-lg tw-h-full tw-bg-white tw-border tw-border-neutral-200 tw-rounded-lg tw-py-4 tw-px-2 tw-max-h-100'>
          <div className='tw-uppercase tw-text-neutral-500 tw-text-sm tw-pb-4 tw-w-full tw-border-b tw-border-neutral-200'>
            Select content
          </div>
          <div className='tw-flex tw-gap-2 tw-pt-6'>
            <div className='tw-w-1/2 -tw-mt-[3px]'>
              <TooltipProvider>
                <Tooltip
                  withArrow
                  sideOffset={0}
                  content={
                    OPTION_GROUP_QUESTIONS[
                      `o${
                        checklistOptions.find(
                          (o) => o.fieldName === activeSection
                        )?.optionGroup || 1
                      }`
                    ]
                  }
                >
                  <TooltipTrigger className='tw-cursor-default'>
                    <div className='tw-uppercase tw-text-neutral-500 tw-text-sm tw-w-full'>
                      Question status
                    </div>
                  </TooltipTrigger>
                </Tooltip>
              </TooltipProvider>
              <div className='tw-flex tw-flex-col tw-gap-6 tw-mt-5'>
                {checklistOptions
                  .find((o) => o.fieldName === activeSection)
                  ?.questionOptions?.map((status) => (
                    <div key={status.fieldName}>
                      <FormField
                        key={status.fieldName}
                        control={form.control}
                        name={status.fieldName as keyof DownloadFormValues}
                        render={({ field }) => (
                          <FormItem
                            className={cn('tw-flex tw-items-center tw-gap-4')}
                          >
                            <FormControl className='tw-flex'>
                              <Checkbox
                                id={`${status}-checkbox`}
                                checked={field.value as boolean}
                                onClick={() =>
                                  onChecklistChange(status.fieldName)
                                }
                                data-testid={`checkbox-${status.fieldName}`}
                              />
                            </FormControl>
                            <div className='tw-flex tw-w-full !tw-mt-0 tw-items-center tw-justify-between tw-gap-2'>
                              <FormLabel
                                onClick={() =>
                                  onChecklistChange(status.fieldName)
                                }
                                className='!tw-my-0 tw-font-normal tw-text-base'
                              >
                                {status.label}
                              </FormLabel>
                            </div>
                          </FormItem>
                        )}
                      />
                    </div>
                  ))}
              </div>
            </div>
            <div className='tw-w-1/2'>
              <div className='tw-uppercase tw-text-neutral-500 tw-text-sm tw-w-full'>
                Information
              </div>
              <div className='tw-flex tw-flex-col tw-gap-6 tw-mt-5'>
                {checklistOptions
                  .find((o) => o.fieldName === activeSection)
                  ?.additionalOptions?.filter((o) => o.show)
                  .map((info) => (
                    <div key={info.fieldName}>
                      <FormField
                        key={info.fieldName}
                        control={form.control}
                        name={info.fieldName as keyof DownloadFormValues}
                        render={({ field }) => (
                          <FormItem
                            className={cn('tw-flex tw-items-center tw-gap-4')}
                          >
                            <FormControl className='tw-flex'>
                              <Checkbox
                                id={`${info.fieldName}-checkbox`}
                                checked={field.value as boolean}
                                onClick={() =>
                                  onChecklistChange(info.fieldName)
                                }
                                data-testid={`checkbox-${info.fieldName}`}
                              />
                            </FormControl>
                            <div className='tw-flex tw-w-full !tw-mt-0 tw-items-center tw-justify-between tw-gap-2'>
                              <FormLabel
                                onClick={() =>
                                  onChecklistChange(info.fieldName)
                                }
                                className='!tw-my-0 tw-font-normal tw-text-base'
                              >
                                {info.label}
                              </FormLabel>
                            </div>
                          </FormItem>
                        )}
                      />
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}
      {activeSection.includes('document') && (
        <div className='tw-w-1/2 tw-max-w-lg tw-h-full tw-bg-white tw-border tw-border-neutral-200 tw-rounded-lg tw-py-4 tw-px-6 tw-max-h-100'>
          <div className='tw-uppercase tw-text-neutral-500 tw-text-sm tw-pb-4 tw-w-full tw-border-b tw-border-neutral-200 -tw-mx-2 tw-px-2'>
            Select content
          </div>
          <div className='tw-flex tw-gap-2 tw-pt-6'>
            <div className='tw-w-full'>
              <div className='tw-flex tw-flex-col tw-gap-2'>
                {documentOption.additionalOptions?.map((info) => (
                  <div key={info.fieldName}>
                    <FormField
                      key={info.fieldName}
                      control={form.control}
                      name={info.fieldName as keyof DownloadFormValues}
                      render={({ field }) => (
                        <FormItem className='tw-flex tw-flex-col tw-mb-2'>
                          <div className='tw-flex tw-items-center tw-gap-2'>
                            <FormControl>
                              <Checkbox
                                id={`${info.fieldName}-checkbox`}
                                checked={field.value as boolean}
                                onClick={() => onChange(info.fieldName)}
                                data-testid={`checkbox-${info.fieldName}`}
                              />
                            </FormControl>
                            <FormLabel
                              onClick={() => onChange(info.fieldName)}
                              className='tw-font-normal tw-text-base'
                            >
                              {info.label}
                            </FormLabel>
                          </div>
                          <span className='tw-text-xs tw-text-neutral-300 tw-pl-6 !tw-mt-0'>
                            {info.description}
                          </span>
                        </FormItem>
                      )}
                    />
                  </div>
                ))}
                {!!documentOption.layoutOptions && (
                  <div>
                    <div className='tw-text-neutral-500 tw-text-sm tw-pb-4  tw-w-full tw-border-b tw-border-neutral-200 -tw-mx-2 tw-px-2 tw-truncate'>
                      <div className='tw-flex tw-items-center tw-gap-2'>
                        <div className='tw-uppercase'>Layout Options</div>
                        <Tooltip
                          content='Selected options will apply to all download pages'
                          withArrow
                        >
                          <Button size='mini' variant='icon'>
                            <Icon
                              icon={faInfoCircle}
                              size='lg'
                              data-testid='icon-info'
                            />
                          </Button>
                        </Tooltip>
                      </div>
                    </div>
                    <div className='tw-flex tw-flex-col tw-gap-6 tw-mt-5 tw-mb-5'>
                      {documentOption.layoutOptions
                        ?.filter((o) => o.show)
                        .map((info) => (
                          <div key={info.fieldName}>
                            <FormField
                              key={info.fieldName}
                              control={form.control}
                              name={info.fieldName as keyof DownloadFormValues}
                              render={({ field }) => (
                                <FormItem className='tw-flex tw-items-center tw-gap-4'>
                                  <FormControl className='tw-flex'>
                                    <Checkbox
                                      id={`${info.fieldName}-checkbox`}
                                      checked={field.value as boolean}
                                      onClick={() => onChange(info.fieldName)}
                                      data-testid={`checkbox-${info.fieldName}`}
                                    />
                                  </FormControl>
                                  <div className='tw-flex tw-w-full !tw-mt-0 tw-items-center tw-justify-between tw-gap-2'>
                                    <FormLabel
                                      onClick={() => onChange(info.fieldName)}
                                      className='!tw-my-0 tw-font-normal tw-text-base'
                                    >
                                      {info.label}
                                    </FormLabel>
                                  </div>
                                </FormItem>
                              )}
                            />
                          </div>
                        ))}
                    </div>
                  </div>
                )}
                <div>
                  <div className='tw-text-neutral-500 tw-text-sm tw-font-medium'>
                    Select PDF compression
                  </div>
                  <Select
                    onValueChange={(value) => onPdfChange(value)}
                    defaultValue={
                      form.watch('useOriginalDoc') ? 'original' : 'processed'
                    }
                  >
                    <SelectTrigger className='tw-w-48 tw-mt-3'>
                      <div>
                        {form.watch('useOriginalDoc')
                          ? 'Original PDF'
                          : 'Processed PDF'}
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='processed' truncate={false}>
                        <div>
                          <div className='tw-font-medium tw-text-sm tw-text-neutral-700'>
                            Processed PDF
                          </div>
                          <div className='tw-text-sm tw-text-neutral-500'>
                            Larger document, but text is selectable
                          </div>
                        </div>
                      </SelectItem>
                      <SelectItem value='original' truncate={false}>
                        <div>
                          <div className='tw-font-medium tw-text-sm tw-text-neutral-700'>
                            Original PDF
                          </div>
                          <div className='tw-text-sm tw-text-neutral-500'>
                            Smaller document, but text may not be selectable
                          </div>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
